// Ported from project/assets/practice-areas.js — 13 practice areas, 3 tiers.
// Add per-area variation via this registry only; never branch in components.

import type { PracticeArea, PracticeAreaId } from "./types";
import { FIRM_CONFIG } from "./firm-config";

export const PRACTICE_AREAS: Record<PracticeAreaId, PracticeArea> = {
  // Tier 1 — primary practice
  ssi: {
    id: "ssi",
    label: "SSI / SSDI",
    shortLabel: "SSI",
    tier: 1,
    accent: "var(--pa-ssi-500)",
    accentSoft: "var(--pa-ssi-50)",
    accentStrong: "var(--pa-ssi-700)",
    icon: "◐",
    pipeline: [
      "Intake",
      "Application",
      "Initial denial",
      "Reconsideration",
      "ALJ hearing",
      "Appeals Council",
      "Federal court",
      "Closed",
    ],
    heroMetric: "aljDate",
    heroMetricLabel: "Next ALJ hearing",
    workspace: "SSIWorkspace",
    actions: ["Upload medical records", "Draft hearing brief", "Vocational expert prep"],
    forms: [
      "SSA-561 (Reconsideration)",
      "HA-501 (ALJ request)",
      "SSA-827 (Medical release)",
      "SSA-3441 (Disability report)",
    ],
    driveFolder: "SSI",
    tagline: "Disability claims — ALJ hearings + Appeals Council",
    bigTip:
      "ALJ hearings make or break the case. Vocational expert prep + RFC pinning matter most.",
  },
  immigration: {
    id: "immigration",
    label: "Immigration",
    shortLabel: "Imm.",
    tier: 1,
    accent: "var(--pa-immigration-500)",
    accentSoft: "var(--pa-immigration-50)",
    accentStrong: "var(--pa-immigration-700)",
    icon: "✦",
    pipeline: [
      "Intake",
      "Filed",
      "Pending",
      "Master Hearing",
      "Individual",
      "Decision",
      "Appeals",
      "Closed",
    ],
    heroMetric: "oneYearDeadline",
    heroMetricLabel: "1-year asylum window",
    workspace: "ImmigrationWorkspace",
    actions: ["Upload docs", "Generate brief", "Interview prep", "Habeas package"],
    subtypes: [
      "asylum",
      "family-petition",
      "naturalization",
      "u-visa",
      "mandamus",
      "habeas",
      "bia-appeal",
      "removal-defense",
      "ead-only",
    ],
    forms: [
      "I-589",
      "I-130",
      "I-485",
      "N-400",
      "I-918",
      "I-765",
      "EOIR-26",
      "EOIR-28",
      "G-28",
      "I-192",
    ],
    driveFolder: "IMMIGRATION",
    tagline: "Asylum, family, naturalization, U-visa, mandamus, habeas, BIA appeals",
    bigTip: "One-year asylum filing deadline is the spine of every defensive case.",
  },
  municipal: {
    id: "municipal",
    label: "Municipal / L-T / Civil",
    shortLabel: "MC",
    tier: 1,
    accent: "var(--pa-municipal-500)",
    accentSoft: "var(--pa-municipal-50)",
    accentStrong: "var(--pa-municipal-700)",
    icon: "▤",
    pipeline: ["Filed", "Service", "Answer", "Mediation", "Hearing", "Judgment", "Appeal/Closed"],
    heroMetric: "courtDate",
    heroMetricLabel: "Next MC hearing",
    workspace: "MunicipalWorkspace",
    actions: ["Draft complaint", "Discovery request", "Hearing prep", "Mitigation"],
    subtypes: ["landlord-tenant", "small-claims", "mc-civil", "code-enforcement", "name-change"],
    forms: ["MC-Complaint", "Answer", "Discovery requests"],
    driveFolder: "Municipal Court",
    tagline: "Landlord-tenant, small claims, MC civil disputes",
    bigTip:
      "Municipal Court runs fast — service of process and hearing dates are the critical deadlines.",
  },
  wills: {
    id: "wills",
    label: "Wills & Estates",
    shortLabel: "Wills",
    tier: 1,
    accent: "var(--pa-wills-500)",
    accentSoft: "var(--pa-wills-50)",
    accentStrong: "var(--pa-wills-700)",
    icon: "✑",
    pipeline: [
      "Intake",
      "Drafting",
      "Review",
      "Notarization",
      "Probate filing",
      "Distribution",
      "Closed",
    ],
    heroMetric: "probateStage",
    heroMetricLabel: "Probate stage",
    workspace: "WillsWorkspace",
    actions: ["Generate will", "Draft POA", "Probate package", "Living will"],
    subtypes: ["will", "poa-financial", "poa-medical", "living-will", "probate", "trust"],
    forms: ["Will", "POA Financial", "POA Medical", "Living Will", "Probate Petition"],
    driveFolder: "Wills",
    tagline: "Wills, POAs, living wills, probate, trusts",
    bigTip: "Most clients want will + POA financial + POA medical + living will as a four-pack.",
  },
  family: {
    id: "family",
    label: "Family Law",
    shortLabel: "Family",
    tier: 1,
    accent: "var(--pa-family-500)",
    accentSoft: "var(--pa-family-50)",
    accentStrong: "var(--pa-family-700)",
    icon: "◈",
    pipeline: ["Intake", "Pleadings", "Discovery", "Motions", "Mediation", "Trial", "Decree"],
    heroMetric: "nextHearing",
    heroMetricLabel: "Next family hearing",
    workspace: "FamilyWorkspace",
    actions: ["Draft complaint", "Custody motion", "Support calc", "PFA petition"],
    subtypes: ["divorce", "custody", "support", "ppo", "adoption", "name-change"],
    forms: ["Divorce Complaint", "Custody Complaint", "Support Calc", "PFA Petition"],
    driveFolder: "DIVORCES + Family Law",
    tagline: "Divorce, custody, support, PFAs, adoption, name change",
    bigTip:
      "Spousal support and child support calcs run from the income evidence — pin paystubs early.",
  },

  // Tier 2 — regular practice
  pi: {
    id: "pi",
    label: "Personal Injury",
    shortLabel: "PI",
    tier: 2,
    accent: "var(--pa-pi-500)",
    accentSoft: "var(--pa-pi-50)",
    accentStrong: "var(--pa-pi-700)",
    icon: "✚",
    pipeline: [
      "Intake",
      "Treatment",
      "Demand sent",
      "Negotiation",
      "Suit filed",
      "Discovery",
      "Settlement",
      "Closed",
    ],
    heroMetric: "demandStatus",
    heroMetricLabel: "Demand status",
    workspace: "PIWorkspace",
    actions: ["Open file", "Generate demand", "Lien resolution", "Settlement breakdown"],
    subtypes: ["mva", "slip-fall", "premises", "drew-referral", "medical-product", "uim-uim"],
    forms: ["Demand letter", "Settlement breakdown", "Lien letter", "PA Civil Complaint"],
    driveFolder: "PERSONAL INJURY + DEMANDS - DREW + New York PI",
    tagline: "MVA, slip-fall, premises liability, Drew-referral demand pipeline",
    bigTip:
      "Statute of limitations is paramount — MVA (PA) = 2 years from accident. Liens resolved at end.",
  },
  criminal: {
    id: "criminal",
    label: "Criminal Defense",
    shortLabel: "Crim",
    tier: 2,
    accent: "var(--pa-criminal-500)",
    accentSoft: "var(--pa-criminal-50)",
    accentStrong: "var(--pa-criminal-700)",
    icon: "⚖",
    pipeline: [
      "Arrest",
      "Arraignment",
      "Pre-trial",
      "Motions",
      "Trial/Plea",
      "Sentencing",
      "Closed",
    ],
    heroMetric: "nextCourtDate",
    heroMetricLabel: "Next court date",
    workspace: "CriminalWorkspace",
    actions: ["Upload discovery", "Draft motion", "Plea analysis (Padilla)", "Bail motion"],
    subtypes: ["felony", "misdemeanor-1", "misdemeanor-2", "misdemeanor-3", "summary"],
    forms: ["Bail motion", "Discovery motion", "Suppression motion", "Plea memo"],
    driveFolder: "CRIMINAL",
    tagline: "Criminal defense — Padilla immigration impact central",
    bigTip:
      "Padilla advisories required for non-citizens. Aggravated felony / CIMT analysis on every plea.",
  },
  traffic: {
    id: "traffic",
    label: "Traffic",
    shortLabel: "Trf",
    tier: 2,
    accent: "var(--pa-traffic-500)",
    accentSoft: "var(--pa-traffic-50)",
    accentStrong: "var(--pa-traffic-700)",
    icon: "▷",
    pipeline: ["Citation", "Plea entered", "Hearing", "Mitigation", "Disposition", "Closed"],
    heroMetric: "courtDate",
    heroMetricLabel: "Hearing date",
    workspace: "TrafficWorkspace",
    actions: ["Upload citation", "Draft mitigation", "Court reschedule", "Points letter"],
    subtypes: ["moving-violation", "non-moving", "cdl", "school-zone"],
    forms: ["Mitigation letter", "Court continuance request"],
    driveFolder: "TRAFFIC",
    tagline: "Citations, points, mitigation, CDL preservation",
    bigTip: "CDL holders take priority — 2 serious violations within 3 years = disqualification.",
  },
  dui: {
    id: "dui",
    label: "DUI",
    shortLabel: "DUI",
    tier: 2,
    accent: "var(--pa-dui-500)",
    accentSoft: "var(--pa-dui-50)",
    accentStrong: "var(--pa-dui-700)",
    icon: "⊘",
    pipeline: [
      "Arrest",
      "Arraignment",
      "Pre-trial",
      "ARD eval",
      "Trial/Plea",
      "License hearing",
      "Closed",
    ],
    heroMetric: "bacOrLicense",
    heroMetricLabel: "BAC / license status",
    workspace: "DUIWorkspace",
    actions: ["Upload BAC report", "ARD application", "License brief", "Ignition interlock"],
    subtypes: ["dui-1st", "dui-2nd", "dui-felony", "refusal", "underage-dui"],
    forms: ["ARD Application", "DL-26B (license appeal)", "Ignition interlock waiver"],
    driveFolder: "DUI",
    tagline: "BAC defense, license suspension, ARD program, ignition interlock",
    bigTip:
      "License suspension hearing is on a separate clock from criminal court — both must be tracked.",
  },

  // Tier 3 — occasional practice
  medmal: {
    id: "medmal",
    label: "Med Mal",
    shortLabel: "MM",
    tier: 3,
    accent: "var(--pa-medmal-500)",
    accentSoft: "var(--pa-medmal-50)",
    accentStrong: "var(--pa-medmal-700)",
    icon: "✛",
    pipeline: [
      "Intake",
      "Records review",
      "Expert review",
      "Pre-suit notice",
      "Suit filed",
      "Discovery",
      "Trial/Settlement",
    ],
    heroMetric: "statuteClock",
    heroMetricLabel: "Statute of repose",
    workspace: "MedMalWorkspace",
    actions: ["Order records", "Expert engagement", "Pre-suit notice", "Damages calc"],
    subtypes: [
      "misdiagnosis",
      "surgical-error",
      "birth-injury",
      "medication-error",
      "nursing-home",
    ],
    forms: ["Pre-suit notice", "Certificate of merit", "Expert report"],
    driveFolder: "Med Mal",
    tagline: "Medical malpractice — statute of limitations + repose driven",
    bigTip:
      "Certificate of Merit required in PA within 60 days of complaint. Expert engagement is the gate.",
  },
  civil: {
    id: "civil",
    label: "Civil Litigation",
    shortLabel: "Civ",
    tier: 3,
    accent: "var(--pa-civil-500)",
    accentSoft: "var(--pa-civil-50)",
    accentStrong: "var(--pa-civil-700)",
    icon: "▢",
    pipeline: ["Intake", "Pleadings", "Discovery", "Motions", "Pre-trial", "Trial", "Judgment"],
    heroMetric: "trialDate",
    heroMetricLabel: "Trial date",
    workspace: "CivilWorkspace",
    actions: ["Draft complaint", "Discovery request", "Trial prep", "Motion practice"],
    subtypes: ["contract", "consumer-protection", "general-civil"],
    forms: ["Civil Complaint", "Discovery requests", "Trial brief"],
    driveFolder: "CIVIL + Enterprise Complaint",
    tagline: "General civil litigation — contract, consumer, complex civil",
    bigTip: "Pre-trial scheduling order drives every deadline; pin it as soon as it issues.",
  },
  appeals: {
    id: "appeals",
    label: "Federal Appeals",
    shortLabel: "App",
    tier: 3,
    accent: "var(--pa-appeals-500)",
    accentSoft: "var(--pa-appeals-50)",
    accentStrong: "var(--pa-appeals-700)",
    icon: "▲",
    pipeline: [
      "Notice of Appeal",
      "Briefing schedule",
      "Opening brief",
      "Response",
      "Reply",
      "Oral arg",
      "Decision",
    ],
    heroMetric: "briefDeadline",
    heroMetricLabel: "Brief deadline",
    workspace: "AppealsWorkspace",
    actions: ["File NOA", "Draft opening brief", "Oral arg prep", "Petition for rehearing"],
    subtypes: ["3cir", "bia", "habeas-appeal", "2cir"],
    forms: ["Notice of Appeal", "Opening Brief", "Reply Brief", "Petition for Rehearing"],
    driveFolder: "APPEAL CONTRACT + Sobel HB",
    tagline: "Third Circuit, BIA, habeas appeals, Second Circuit",
    bigTip: "FRAP 32 word limits + LAR requirements. Midpage-verified citations on every brief.",
  },
  bankruptcy: {
    id: "bankruptcy",
    label: "Bankruptcy",
    shortLabel: "BK",
    tier: 3,
    accent: "var(--pa-bk-500)",
    accentSoft: "var(--pa-bk-50)",
    accentStrong: "var(--pa-bk-700)",
    icon: "▭",
    pipeline: [
      "Intake",
      "Means test",
      "Schedules",
      "Filed",
      "341 meeting",
      "Confirmation",
      "Discharge",
    ],
    heroMetric: "filingType",
    heroMetricLabel: "Chapter filed",
    workspace: "BankruptcyWorkspace",
    actions: ["Means test", "Draft schedules", "341 prep", "Discharge motion"],
    subtypes: ["chapter-7", "chapter-13", "chapter-11"],
    forms: ["Petition", "Schedules A-J", "Statement of Financial Affairs", "Means Test"],
    driveFolder: "BANKRUPTCY",
    tagline: "Chapter 7, 13 — consumer bankruptcy",
    bigTip: "Means test gates Chapter 7 eligibility. Schedule completeness avoids 341 surprises.",
  },
};

export const PRACTICE_AREA_ORDER: PracticeAreaId[] = [
  "ssi",
  "immigration",
  "municipal",
  "wills",
  "family",
  "pi",
  "criminal",
  "traffic",
  "dui",
  "medmal",
  "civil",
  "appeals",
  "bankruptcy",
];

export const practiceAreaById = (id: string): PracticeArea | null =>
  (PRACTICE_AREAS as Record<string, PracticeArea>)[id] || null;

export const practiceAreaLabel = (id: string): string =>
  practiceAreaById(id)?.label ?? (id === OTHER_AREA.id ? OTHER_AREA.label : id);

export const activePracticeAreas = (active?: PracticeAreaId[]): PracticeArea[] =>
  (active ?? PRACTICE_AREA_ORDER).map((id) => PRACTICE_AREAS[id]).filter(Boolean);

// The firm's config is a menu, not a claim about the caseload.
export const areasWithMatters = (counts: Record<string, number>): PracticeArea[] =>
  activePracticeAreas(FIRM_CONFIG.activePracticeAreas).filter((p) => (counts[p.id] ?? 0) > 0);

/** The server's Other bucket: real matters, no registry entry, no page - its list is at `to`. */
export const OTHER_AREA = {
  id: "other",
  label: "Other",
  shortLabel: "Other",
  accent: "var(--muted)",
  accentStrong: "var(--muted)",
  to: "/cases?area=other",
};

// CSS-var bag for the practice-area accent — set on a container, all
// descendants pick it up without React re-renders.
export const paStyle = (areaId: string): React.CSSProperties | undefined => {
  const a = practiceAreaById(areaId);
  if (!a) return undefined;
  return {
    "--pa-accent": a.accent,
    "--pa-accent-soft": a.accentSoft,
    "--pa-accent-strong": a.accentStrong,
  } as React.CSSProperties;
};

import type * as React from "react";
